'use client';

import '@/features/cards/tokens';

import { seriesClient } from '@/api/client/series.client';
import { queryKeys } from '@/api/query-keys';
import { SeriesCard, type SeriesCardData } from '@/features/cards';
import type { FlagCode } from '@/features/cards/primitives/Flag';
import { countryName, resolveVideoCountryIso } from '@/shared/utils/country-iso';
import { getSeriesRoute } from '@/shared/utils/formatting';
import { getChannelHandle, getCreatorRoute } from '@/shared/utils/routes';
import type { Series, Video } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, type ReactElement } from 'react';
import { useSeriesWatchlistToggle } from './use-series-watchlist-toggle';

const MAX_COUNTRIES = 4;

interface SeriesCardContainerProps {
  series: Series;
  /**
   * Intent: user clicked More Info in HoverActions. Typical use: open
   * the SeriesInfoModal.
   */
  onOpenInfo?: (series: Series) => void;
  /** Load the thumbnail now at high fetch priority; only when the grid is the page LCP. */
  eager?: boolean;
  sizes?: string;
}

function deriveCountries(videos: Video[] | undefined): ReadonlyArray<FlagCode> {
  if (videos === undefined) return [];
  const seen = new Set<FlagCode>();
  for (const v of videos) {
    const iso = resolveVideoCountryIso(v);
    if (iso !== undefined && !seen.has(iso)) {
      seen.add(iso);
      if (seen.size >= MAX_COUNTRIES) break;
    }
  }
  return Array.from(seen);
}

function composeRegionLabel(codes: ReadonlyArray<FlagCode>): string | undefined {
  if (codes.length === 0) return undefined;
  if (codes.length === 1 && codes[0] !== undefined) return countryName(codes[0]);
  return `${codes.length} countries`;
}

function adapt(series: Series): SeriesCardData {
  const thumbnail = series.trailer_thumbnail ?? series.thumbnail ?? series.card_thumbnail ?? '';
  const title = series.title ?? '';

  const countryCodes = deriveCountries(series.videos);
  const regionLabel = composeRegionLabel(countryCodes);

  const episodeCount = series.videos_count ?? 0;
  const episodesLabel =
    episodeCount > 0 ? `${episodeCount} episode${episodeCount === 1 ? '' : 's'}` : undefined;

  const creatorName = series.channel?.name;
  const creatorAvatarSrc = series.channel?.dp;
  const creatorHandle = getChannelHandle(series.channel);
  const creatorHref =
    typeof creatorHandle === 'string' && creatorHandle.length > 0
      ? getCreatorRoute(creatorHandle)
      : undefined;

  const isNew = series.latest === true;

  return {
    frame: 'stacked',
    href: getSeriesRoute(series.id, series.title),
    thumbnailSrc: thumbnail,
    thumbnailAlt: title || 'Series',
    title,
    ...(isNew ? { format: 'NEW', formatTone: 'new' as const } : {}),
    ...(countryCodes.length > 0 ? { countries: countryCodes } : {}),
    ...(regionLabel !== undefined ? { regionLabel } : {}),
    ...(episodesLabel !== undefined ? { episodesLabel } : {}),
    ...(creatorName !== undefined && creatorName.length > 0 ? { creatorName } : {}),
    ...(creatorAvatarSrc !== undefined && creatorAvatarSrc.length > 0 ? { creatorAvatarSrc } : {}),
    ...(creatorHref !== undefined ? { creatorHref } : {}),
  };
}

export function SeriesCardContainer({
  series,
  onOpenInfo,
  eager,
  sizes,
}: SeriesCardContainerProps): ReactElement {
  const queryClient = useQueryClient();
  const data = useMemo(() => adapt(series), [series]);

  const handlePrefetch = useCallback((): void => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.series.detail(series.id),
      queryFn: () => seriesClient.getDetail(series.id),
      staleTime: 1000 * 60 * 5,
    });
  }, [queryClient, series.id]);

  const handleOpenInfo = useCallback((): void => {
    onOpenInfo?.(series);
  }, [onOpenInfo, series]);

  const watchlistProps = useSeriesWatchlistToggle(series, 'series');

  return (
    <SeriesCard
      series={data}
      onPrefetch={handlePrefetch}
      {...watchlistProps}
      {...(onOpenInfo !== undefined ? { onOpenPreview: handleOpenInfo } : {})}
      {...(eager !== undefined ? { eager } : {})}
      {...(sizes !== undefined ? { sizes } : {})}
    />
  );
}
